import React, { createContext, useContext, useState, useEffect } from 'react';

interface User {
  id: number;
  name: string;
  email: string;
  created_at: string;
}

interface UserContextType {
  user: User | null;
  setUser: (user: User | null) => void;
  isAuthenticated: boolean;
  login: (email: string, name?: string) => Promise<void>;
  logout: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    // Try to load user from localStorage on app start
    const savedUser = localStorage.getItem('email-manager-user');
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (err) {
        console.error('Failed to parse saved user:', err);
        localStorage.removeItem('email-manager-user');
      }
    } else {
      // Auto-login as demo user for now
      setUser({
        id: 1,
        name: 'Demo User',
        email: 'demo@email-manager.app',
        created_at: new Date().toISOString()
      });
    }
  }, []);

  const login = async (email: string, name?: string) => {
    try {
      // For now, just create/login as the user
      // In a real app, this would hit the /api/auth/login endpoint
      const userData: User = {
        id: 1, // Default user ID
        name: name || 'User',
        email,
        created_at: new Date().toISOString()
      };
      
      setUser(userData);
      localStorage.setItem('email-manager-user', JSON.stringify(userData));
    } catch (err) {
      throw new Error('Login failed');
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('email-manager-user');
  };

  return (
    <UserContext.Provider value={{
      user,
      setUser,
      isAuthenticated: !!user,
      login,
      logout
    }}>
      {children}
    </UserContext.Provider>
  );
};